import './polyfills';

import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { isProdMode, versionInfo, amBaseUrl } from './config';
import { apiProviders } from './api';
import { serviceProviders } from './services';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { ServerErrorInterceptor } from './interceptors/server-error.interceptor';
import { ServerErrorGuard } from './guards/server-error.guard';
import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import {
  MultipleTranslatePoHttpLoader,
  AppGuard,
  UserRightsModule,
  ShellV2Module,
  ActionMenuModule,
  ButtonV2Module,
  CheckboxV2Module,
  DateV2Module,
  GridV2Module,
  IconV2Module,
  ModalModule,
  MultiselectV2Module,
  InputV2Module,
  RadioV2Module,
  SideDrawerModule,
  SpinnerV2Module,
  PipesModule,
  AssetsModule as AssetModule,
  PagesV2Module,
  UnitsModule,
  FileUploadV2Module,
  AssetsModule,
  FormsRootModuleV2,
  ShellV2Component,
} from '@schenck/rotec-components';
import { provideAnimations } from '@angular/platform-browser/animations';
import { withHashLocation, provideRouter, Routes } from '@angular/router';
import { PagesComponent } from './pages/pages.component';
import { routes } from './routes';
import { overviewRoutes, OverviewModule } from './pages/overview/overview.module';
import { machineTasksRoutes, MachineTasksModule } from './pages/machine-tasks/machine-tasks.module';
import {
  machineHistoryRoutes,
  MachineHistoryModule,
} from './pages/machine-history/machine-history.module';
import { toolingTasksRoutes, ToolingTasksModule } from './pages/tooling-tasks/tooling-tasks.module';
import {
  toolingHistoryRoutes,
  ToolingHistoryModule,
} from './pages/tooling-history/tooling-history.module';
import { CommonModule, registerLocaleData } from '@angular/common';
import { environment } from './environment/environment';
import { MatRadioModule } from '@angular/material/radio';
import { SchedulingFormFieldComponent } from './components/scheduling/scheduling';
import { AlarmFormFieldComponent } from './components/scheduling-alarm/alarm';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { bootstrapApplication } from '@angular/platform-browser';

import deLocale from '@angular/common/locales/de';
import enLocale from '@angular/common/locales/en';
import esLocale from '@angular/common/locales/es';
import frLocale from '@angular/common/locales/fr';
import itLocale from '@angular/common/locales/it';
import nlLocale from '@angular/common/locales/nl';
import svLocale from '@angular/common/locales/sv';
import plLocale from '@angular/common/locales/pl';
import trLocale from '@angular/common/locales/tr';
import zhLocale from '@angular/common/locales/zh';
import csLocale from '@angular/common/locales/cs';
import huLocale from '@angular/common/locales/hu';
import arLocale from '@angular/common/locales/ar';
import koLocale from '@angular/common/locales/ko';
import jaLocale from '@angular/common/locales/ja';
import ptLocale from '@angular/common/locales/pt';

registerLocaleData(deLocale, 'de');
registerLocaleData(enLocale, 'en');
registerLocaleData(esLocale, 'es');
registerLocaleData(frLocale, 'fr');
registerLocaleData(itLocale, 'it');
registerLocaleData(nlLocale, 'nl');
registerLocaleData(svLocale, 'sv');
registerLocaleData(plLocale, 'pl');
registerLocaleData(trLocale, 'tr');
registerLocaleData(zhLocale, 'zh');
registerLocaleData(csLocale, 'cs');
registerLocaleData(huLocale, 'hu');
registerLocaleData(arLocale, 'ar');
registerLocaleData(koLocale, 'ko');
registerLocaleData(jaLocale, 'ja');
registerLocaleData(ptLocale, 'pt');

function HttpPoLoaderFactory(http: HttpClient) {
  return new MultipleTranslatePoHttpLoader(http, [
    { prefix: 'assets/rotec-locales/', suffix: '.po' },
    { prefix: 'assets/locales/', suffix: '.po' },
  ]);
}

const appRoutes: Routes = [
  {
    path: '',
    component: PagesComponent,
    canActivate: [AppGuard],
    children: [
      { path: routes.overview.url, children: overviewRoutes },
      { path: routes.machineTasks.url, children: machineTasksRoutes },
      { path: routes.machineHistory.url, children: machineHistoryRoutes },
      { path: routes.toolingTasks.url, children: toolingTasksRoutes },
      { path: routes.toolingHistory.url, children: toolingHistoryRoutes },
      { path: '**', redirectTo: routes.overview.url },
    ],
  },
  { path: '**', redirectTo: routes.overview.url },
];

if (isProdMode) {
  enableProdMode();
}

bootstrapApplication(ShellV2Component, {
  providers: [
    provideZoneChangeDetection(),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpPoLoaderFactory,
        deps: [HttpClient],
      },
    }),
    importProvidersFrom(
      CommonModule,
      // Rotec-Components Library
      UserRightsModule.forRoot(environment.umBaseUrl),
      ShellV2Module.forRoot({
        isProduction: isProdMode,
        appLogoAndName: {
          name: 'Maintenance Assistant',
          logoUrl: './assets/img/maintenanceassistant.png',
        },
        versionInfo,
        msalConfig: environment.msalConfig,
        azureEndpoint: environment.azureEndpoint,
        baseUrl: environment.apiBaseUrl,
        usBaseUrl: environment.usBaseUrl,
      }),
      ActionMenuModule,
      ButtonV2Module,
      CheckboxV2Module,
      DateV2Module,
      GridV2Module,
      IconV2Module,
      ModalModule.forRoot(),
      MultiselectV2Module,
      InputV2Module,
      RadioV2Module,
      SideDrawerModule.forRoot(),
      SpinnerV2Module.forRoot(),
      InputV2Module,
      PipesModule,
      AssetModule.forRoot(amBaseUrl),
      MatRadioModule,
      PagesV2Module,
      UnitsModule.forRoot(),
      FileUploadV2Module.forRoot({
        uploadBaseUrl: amBaseUrl,
        fallbackImgSrcForPipe: './assets/img/myschenckone-hoch.png',
        fallbackImgSrcForFileUpload: './assets/img/favicon.ico',
        uploadApi: 'files',
      }),
      AssetsModule.forRoot(amBaseUrl),
      FormsRootModuleV2.withAdditionalConfig({
        types: [
          { name: 'scheduling', component: SchedulingFormFieldComponent },
          { name: 'alarm', component: AlarmFormFieldComponent },
        ],
      }),
      DragDropModule,
      // Own
      OverviewModule,
      MachineTasksModule,
      MachineHistoryModule,
      ToolingTasksModule,
      ToolingHistoryModule
    ),
    ...apiProviders,
    ...serviceProviders,
    { provide: HTTP_INTERCEPTORS, useClass: ServerErrorInterceptor, multi: true },
    ServerErrorGuard,
    provideAnimations(),
    provideRouter(appRoutes, withHashLocation()),
  ],
}).catch((err) => console.log(err));
